import { useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { motion } from "framer-motion";
import { useFetchData } from "../../../hooks/useFetchData";
import { CircularProgressbar } from "react-circular-progressbar";
import { useAuthContext } from "../../../hooks/useAuthContext";
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateForm = (formData, selectedProduct, level, stats) => {
  const errors = [];
  
  // Existing validations
  if (!formData.username.trim()) errors.push("اسم العميل مطلوب");
  if (!formData.email.trim()) errors.push("البريد الإلكتروني مطلوب");
  if (!formData.phoneNumber.trim()) errors.push("رقم الهاتف مطلوب");
  if (!formData.quantity || formData.quantity <= 0) errors.push("الكمية يجب أن تكون أكبر من 0");
  if (!selectedProduct) errors.push("يرجى اختيار المنتج");
  
  // Stock validation
  if(!level?.level || !stats?.data) {
    errors.push("data not available");
    
  }
  if (level?.level?.targets && stats?.data?.totalSalesByCategoryForCurrentMonth && selectedProduct) {
    // Find the target for the selected product's category
    console.log("inside the function");
    const target = level.level.targets.find(t => 
      t.category.name === selectedProduct.categories[0]
    );
    console.log(target);
    

    if (target) {
      // Find current sales for this category
      const currentSales = stats.data.totalSalesByCategoryForCurrentMonth.find(
        s => s.category.name === selectedProduct.categories[0]
      );

      const currentSalesQuantity = currentSales?.totalQuantity || 0;
      const newSaleQuantity = formData.quantity * selectedProduct.unitValue;
      const maxDeliveryQuantity = target.kgToDeliver;

      if (currentSalesQuantity + newSaleQuantity > maxDeliveryQuantity) {
        errors.push(`لا يوجد مخزون كافي. المتبقي المتاح: ${maxDeliveryQuantity - currentSalesQuantity} كغ`);
      }
    }
  }

  // Email and phone validations remain the same
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (formData.email && !emailRegex.test(formData.email)) {
    errors.push("البريد الإلكتروني غير صالح");
  }

  const phoneRegex = /^[0-9+\s-]{8,}$/;
  if (formData.phoneNumber && !phoneRegex.test(formData.phoneNumber)) {
    errors.push("رقم الهاتف غير صالح");
  }

  return errors;
};

function Transaction() {
  const { auth } = useAuthContext();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phoneNumber: "",
    quantity: 0,
    category: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedProduct, setProduct] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const { data: categories } = useFetchData("client-api/transaction-products");
  const { data: categ } = useFetchData("client-api/categories");
  const { data: stats } = useFetchData("mandoub/honeyTransaction", [auth, refetchTrigger]);
  const { data: level } = useFetchData("mandoub/leveldata?id=" + auth?.user._id, [auth, refetchTrigger]);

  const getCateg = useCallback((product, categ) => {
    if (!categ || !product || !product.categories[0]) return null;
    const categoryId = categ.filter((element) => element.name === product.categories[0]);
    return categoryId[0];
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setError(null); // Clear errors when user makes changes
  };

  const handleReset = () => {
    setFormData({
      username: "",
      email: "",
      phoneNumber: "",
      quantity: 0,
      category: "",
    });
    setError(null);
    setSuccess(null);
    setProduct(null);
  };

  

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    // Validate form
    const validationErrors = validateForm(formData, selectedProduct, level,stats);
    if (validationErrors.length > 0) {
      setError(validationErrors.join("\n"));
      setLoading(false);
      return;
    }

    try {
      const category = getCateg(selectedProduct, categ?.categories);
      if (!category) {
        throw new Error("فئة المنتج غير صالحة");
      }
      

      // Create transaction
      const transactionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/mandoub/honeyTransaction`,
        {
          clientName: formData.username,
          clientEmail: formData.email,
          clientPhone: formData.phoneNumber,
          qte: formData.quantity * selectedProduct.unitValue,
          fromOrders: false,
          category: category._id,
        },
        {
          headers: {
              Authorization : `Bearer ${JSON.parse(localStorage.getItem('authMandoub'))?.token}`
          },
          withCredentials: true,
      }      );

      if (transactionResponse.data) {
        // Create order
        const orderData = {
          coupon: null,
          products: [{ ...selectedProduct, qteToBuy: formData.quantity }],
          billingInfo: {},
          shippingInfo: {},
          staff: auth.user._id || "",
          deliveryInfo: {
            cost: 0,
            method: "direct",
          },
          paymentData: {
            method: "نقداً",
          },
          totalPrice: selectedProduct.discount * formData.quantity,
          payAmount: selectedProduct.discount * formData.quantity,
          vat: 0,
          tax: 0,
        };

        await axios.post(
          `${process.env.REACT_APP_API_URL}mandoub/direct-order`,
          orderData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        setRefetchTrigger(prev => prev + 1);
        toast.success('تم تحديث المعلومات بنجاح!');
        handleReset();
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشلت العملية. يرجى المحاولة مرة أخرى.");
      console.error("Transaction error:", err);
      toast.error('حدث خطأ أثناء تحديث معلوماتك.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="df ai-stretch trans-form">
      <ToastContainer />
      <div className="section-card w-50 tf">
        <div className="title">قم بعملية مباشرة</div>
        
        <div className="df-c w-full pr">
          <label htmlFor="username" className="label-trans">اسم العميل</label>
          <input
            type="text"
            name="username"
            className="w-full"
            value={formData.username}
            onChange={handleChange}
            id="username"
            disabled={loading}
          />
        </div>

        <div className="df-c w-full pr">
          <label htmlFor="email" className="label-trans">بريد العميل الإلكتروني</label>
          <input
            type="email"
            name="email"
            className="w-full"
            value={formData.email}
            onChange={handleChange}
            id="email"
            disabled={loading}
          />
        </div>

        <div className="df-c w-full pr">
          <label htmlFor="phoneNumber" className="label-trans">رقم هاتف العميل</label>
          <input
            type="tel"
            name="phoneNumber"
            className="w-full"
            value={formData.phoneNumber}
            onChange={handleChange}
            id="phoneNumber"
            disabled={loading}
          />
        </div>

        <div className="df w-100">
          <Grid item xs={6} className="w-50">
            <FormControl fullWidth>
              <InputLabel className="labeled">العسل</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={loading}
              >
                {categories?.products?.map((category) => (
                  <MenuItem 
                    value={category._id} 
                    key={category._id} 
                    onClick={() => setProduct(category)} 
                    className="icon-box df"
                  >
                    <img 
                      src={category?.image?.[0]?.url} 
                      style={{ height: '2rem', width: '2rem', objectFit: 'cover' }} 
                      alt={category.name}
                    />
                    <div className="df">
                      <div className="text">{category.name}</div>
                      <div className="font-bold">
                        {category.price} ريال  - {category.unitValue} كغ
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <div className="df-c pr flex1">
            <label htmlFor="quantity" className="label-trans">الكمية للبيع</label>
            <input
              type="number"
              name="quantity"
              className="w-full"
              value={formData.quantity}
              onChange={handleChange}
              id="quantity"
              min="0"
              disabled={loading}
            />
          </div>
        </div>

        <div className="df header-btns mr-auto mt-auto">
          <motion.div
            className="details-btn df"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleReset}
            style={{ opacity: loading ? 0.5 : 1 }}
          >
            إعادة تعيين
          </motion.div>
          
          {categ && selectedProduct && categories && (
            <motion.button
              className="details-btn df"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleSubmit}
              disabled={loading}
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              {loading ? "جارٍ التنفيذ..." : "تأكيد"}
            </motion.button>
          )}
        </div>

        {error && <div className="error mt-4">{error}</div>}
        {success && <div className="success mt-4">تمت العملية بنجاح</div>}
      </div>

      {level && stats?.data?.totalSalesByCategoryForCurrentMonth && stats?.data?.totalHoneyGivenForCurrentMonth && (
        <div className="section-card qte-left !px-12 jc-sb w-50">
          <div className="df-c">
            <div className="bold-1 text-indigo-700">مخزونك الحالي</div>
            <div className="df-c">
              <div className="df-c jc-sa pr">
                {level.level.targets.map((target) => {
                  const matchingSalesItem = stats.data.totalSalesByCategoryForCurrentMonth.find(
                    salesItem => salesItem.category._id === target.category._id
                  );
                  const percentage = Math.min(
                    ((matchingSalesItem?.totalQuantity || 0) / target.kgToDeliver * 100).toFixed(2),
                    100
                  );

                  return (
                    <div key={target._id} className="flex items-center gap-5 p-4 bg-blue-100 rounded bonus">
                      <div className="flex flex-shrink-0 items-center justify-center bg-blue-300 h-16 w-16 rounded">
                        <img 
                          src={target.category.icon[0].url} 
                          className="w-12 h-12 object-cover" 
                          alt={target.category.name} 
                        />
                      </div>
                      <div className="flex-grow flex flex-col ml-4">
                        <span className="text-xl font-bold">
                          {target.kgToDeliver} كغ {target.category.name}
                        </span>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">
                            {matchingSalesItem?.totalQuantity || 0}/{target.monthlyTarget} تم التوصيل
                          </span>
                        </div>
                      </div>
                      <div className="radialBar">
                        <CircularProgressbar 
                          value={percentage} 
                          text={`${Math.round(percentage)}%`} 
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Transaction;